import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, ChartLineUp, Shield, Brain, Check, Sparkle,
  Users, Gear, Target, Lightning, Clock, Handshake,
  ChartBar, ClipboardText, Robot, Buildings, UserCircle,
  CalendarCheck, Pulse, FileText, TrendUp, Eye
} from "@phosphor-icons/react";

const ServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const coreServices = [
    {
      id: "operations",
      icon: ChartLineUp,
      title: "Hospice Operations Optimization",
      gradient: "from-cyan-500 to-blue-600",
      bgGradient: "from-cyan-500/10 to-blue-600/10",
      overview: "We help hospice organizations build high-performing teams, sustainable workflows, and scalable operations. Our approach focuses on improving the systems that drive census growth, team productivity, and long-term organizational health.",
      challenges: [
        "Census stagnation despite market opportunity",
        "RN case manager burnout and turnover",
        "Inefficient admissions and referral workflows",
        "Lack of operational visibility for leadership",
        "Unsustainable PRN utilization rates"
      ],
      offerings: [
        "Admissions and referral workflow optimization",
        "RN case manager workload and productivity modeling",
        "Staffing strategy and PRN utilization reduction",
        "Caseload capacity planning",
        "Visit productivity benchmarking",
        "Leadership operational frameworks",
        "Census growth strategy development"
      ],
      outcomes: [
        "Increased census capacity without proportional staff growth",
        "Improved team sustainability and reduced burnout",
        "Stronger operational margins",
        "Clearer leadership accountability structures",
        "Scalable systems that support growth"
      ]
    },
    {
      id: "compliance",
      icon: Shield,
      title: "Compliance & Regulatory Implementation",
      gradient: "from-indigo-500 to-purple-600",
      bgGradient: "from-indigo-500/10 to-purple-600/10",
      overview: "Regulations are not obstacles. They are operational frameworks. We help hospice organizations translate CMS Conditions of Participation, state requirements, and accreditation standards into practical systems that protect your organization and support your clinical teams.",
      challenges: [
        "Survey anxiety and lack of readiness",
        "Inconsistent documentation practices",
        "Reactive compliance instead of proactive systems",
        "Leadership uncertainty on regulatory requirements",
        "Poorly designed QAPI programs"
      ],
      offerings: [
        "Survey readiness preparation and mock surveys",
        "HOPE tool implementation strategy",
        "QAPI program design and optimization",
        "Incident reporting system design",
        "Compliance leadership training",
        "Policy and workflow development",
        "Plan of Correction implementation support"
      ],
      outcomes: [
        "Confident survey readiness at all times",
        "Improved documentation processes across teams",
        "Reduced compliance risk and exposure",
        "Structured quality oversight systems",
        "Clear leadership accountability for regulatory performance"
      ]
    },
    {
      id: "ai-intelligence",
      icon: Brain,
      title: "AI Workflow Design & Operational Intelligence",
      gradient: "from-emerald-500 to-teal-600",
      bgGradient: "from-emerald-500/10 to-teal-600/10",
      overview: "Healthcare AI should solve real problems, not create new ones. We help organizations implement practical, AI-enabled systems that improve operational visibility, reduce administrative burden, and support clinical decision-making without disrupting care delivery.",
      challenges: [
        "Limited visibility into operational performance",
        "Administrative burden on clinical staff",
        "Slow decision-making due to fragmented data",
        "Manual processes that don't scale",
        "Technology solutions that don't fit healthcare workflows"
      ],
      offerings: [
        "AI-powered incident reporting workflows",
        "Operational dashboards for leadership (HospiceCareLens)",
        "Home Health Operational System implementation",
        "Predictive staffing and workload analytics",
        "Custom system development to fit your workflows",
        "Documentation automation frameworks",
        "Admissions workflow automation",
        "Executive intelligence dashboards"
      ],
      outcomes: [
        "Real-time operational visibility for leadership",
        "Reduced administrative burden on clinical teams",
        "Faster, data-informed decision-making",
        "Improved team coordination and communication",
        "Scalable systems built for organizational growth"
      ]
    }
  ];

  const engagementModels = [
    {
      icon: CalendarCheck,
      title: "Strategy Sessions",
      description: "Focused working sessions to address specific operational challenges, clarify priorities, and develop action plans."
    },
    {
      icon: Eye,
      title: "Operational Audits",
      description: "Comprehensive assessment of operations, compliance, and systems to identify opportunities and risks."
    },
    {
      icon: Gear,
      title: "Implementation Projects",
      description: "Hands-on support to design, build, and deploy operational systems, workflows, and technology solutions."
    },
    {
      icon: Handshake,
      title: "Leadership Advisory Retainers",
      description: "Ongoing strategic partnership providing consistent access to expert guidance and operational support."
    },
    {
      icon: Robot,
      title: "System Design & Deployment",
      description: "End-to-end design and implementation of AI-enabled systems and operational intelligence platforms."
    }
  ];

  const idealClients = [
    "Hospice agencies experiencing growth and needing scalable operations",
    "Organizations preparing for state surveys or accreditation",
    "Leadership teams strengthening compliance infrastructure",
    "Agencies seeking operational modernization and efficiency",
    "Organizations exploring AI-enabled healthcare operations"
  ];

  return (
    <div data-testid="services-page" className="overflow-hidden">
      {/* Hero Section - Futuristic */}
      <section className="section-padding bg-slate-50 relative overflow-hidden" data-testid="services-hero">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-mesh-gradient" />
        <div className="absolute inset-0 decorative-grid opacity-30" />
        
        <motion.div 
          className="absolute top-20 right-1/4 w-96 h-96 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="max-w-7xl mx-auto container-padding relative">
          <motion.div
            className="max-w-4xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="badge badge-accent mb-8">
              <Target weight="fill" className="w-4 h-4 mr-2" />
              Strategic Healthcare Solutions
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-slate-900 mb-8 leading-[1.05] tracking-tight">
              Strategic Solutions for{" "}
              <span className="text-gradient">Growth & Compliance</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-10">
              We help hospice and home health organizations scale sustainably, strengthen 
              compliance, improve operational visibility, and implement intelligent systems 
              that support clinical teams and drive organizational performance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/schedule-demo"
                className="btn-accent inline-flex items-center justify-center gap-2"
                data-testid="services-cta-consultation"
              >
                Schedule a Strategy Consultation
                <ArrowRight weight="bold" className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="section-padding bg-white" data-testid="approach-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="badge badge-accent mb-6">Our Approach</span>
              <h2 className="font-display text-4xl md:text-5xl text-slate-900 mb-8 tracking-tight">
                Integrated Strategy, Not Fragmented Solutions
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Most healthcare organizations struggle because operations, compliance, and 
                technology are treated as separate challenges with separate solutions. This 
                creates fragmentation, inefficiency, and risk.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-10">
                We take a different approach. Our firm integrates real-world hospice leadership 
                experience, regulatory expertise, operational strategy, and AI-enabled systems 
                design into one cohesive framework.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Users, label: "Hospice Leadership" },
                  { icon: Shield, label: "Regulatory Expertise" },
                  { icon: ChartLineUp, label: "Operational Strategy" },
                  { icon: Brain, label: "AI Systems Design" }
                ].map((item, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-center gap-3 bg-slate-50 rounded-xl p-4 border border-slate-100"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-cyan-500/10 to-blue-500/10 flex items-center justify-center">
                      <item.icon weight="duotone" className="w-5 h-5 text-cyan-600" />
                    </div>
                    <span className="text-sm font-medium text-slate-900">{item.label}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-3xl blur-lg opacity-20" />
              <div className="relative bg-slate-900 rounded-3xl p-10 text-white">
                <h3 className="font-display text-2xl mb-8">The Integration Advantage</h3>
                <div className="space-y-5">
                  {[
                    "Operations inform compliance design",
                    "Compliance systems support operational efficiency",
                    "Technology amplifies both without disruption",
                    "Leadership gains clarity across all domains"
                  ].map((item, index) => (
                    <motion.div 
                      key={index} 
                      className="flex items-start gap-4"
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="w-6 h-6 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                        <Check weight="bold" className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-slate-300">{item}</span>
                    </motion.div>
                  ))}
                </div>
                <div className="mt-10 pt-8 border-t border-slate-700">
                  <p className="text-slate-400 text-sm leading-relaxed">
                    The result: healthcare organizations that are operationally sound, 
                    regulatory confident, and positioned for sustainable growth.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Services Section - Bento Style */}
      <section className="section-padding bg-slate-50" data-testid="core-services">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="badge badge-accent mb-4">Core Services</span>
            <h2 className="font-display text-4xl md:text-5xl text-slate-900 mb-6 tracking-tight">
              Three Integrated Service Pillars
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Each service area is designed to work independently or as part of a comprehensive 
              engagement, depending on your organization's needs and priorities.
            </p>
          </motion.div>

          <div className="space-y-12">
            {coreServices.map((service, index) => (
              <motion.div
                key={service.id}
                className="bg-white rounded-3xl overflow-hidden shadow-lg border border-slate-100"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`service-${service.id}`}
              >
                {/* Service Header */}
                <div className={`bg-gradient-to-r ${service.gradient} p-8 md:p-12`}>
                  <div className="flex items-center gap-5 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <service.icon weight="fill" className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-display text-3xl md:text-4xl text-white">{service.title}</h3>
                  </div>
                  <p className="text-white/90 text-lg leading-relaxed max-w-4xl">
                    {service.overview}
                  </p>
                </div>

                {/* Service Content */}
                <div className="p-8 md:p-12">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                    {/* Challenges */}
                    <div>
                      <h4 className="font-display font-bold text-slate-900 mb-6 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                          <Target weight="duotone" className="w-4 h-4 text-red-500" />
                        </div>
                        Challenges We Solve
                      </h4>
                      <ul className="space-y-4">
                        {service.challenges.map((challenge, i) => (
                          <li key={i} className="flex items-start gap-3 text-slate-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0 mt-2" />
                            {challenge}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Offerings */}
                    <div>
                      <h4 className="font-display font-bold text-slate-900 mb-6 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center">
                          <Gear weight="duotone" className="w-4 h-4 text-cyan-600" />
                        </div>
                        Service Offerings
                      </h4>
                      <ul className="space-y-4">
                        {service.offerings.map((offering, i) => (
                          <li key={i} className="flex items-start gap-3 text-slate-600">
                            <Check weight="bold" className="w-4 h-4 text-cyan-500 flex-shrink-0 mt-0.5" />
                            {offering}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Outcomes */}
                    <div>
                      <h4 className="font-display font-bold text-slate-900 mb-6 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <TrendUp weight="duotone" className="w-4 h-4 text-emerald-500" />
                        </div>
                        Expected Outcomes
                      </h4>
                      <ul className="space-y-4">
                        {service.outcomes.map((outcome, i) => (
                          <li key={i} className="flex items-start gap-3 text-slate-600">
                            <Sparkle weight="fill" className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                            {outcome}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Models Section */}
      <section className="section-padding bg-white" data-testid="engagement-models">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="badge badge-accent mb-4">How We Work</span>
            <h2 className="font-display text-4xl md:text-5xl text-slate-900 mb-6 tracking-tight">
              Engagement Models
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We structure engagements based on your organization's needs, timeline, and strategic priorities.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {engagementModels.map((model, index) => (
              <motion.div
                key={index}
                className="bg-slate-50 rounded-2xl p-6 hover:bg-slate-100 transition-all border border-slate-100 hover:border-cyan-500/30 hover:shadow-lg group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10 group-hover:from-cyan-500 group-hover:to-blue-500 flex items-center justify-center mb-5 transition-all">
                  <model.icon weight="duotone" className="w-6 h-6 text-cyan-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-display font-bold text-slate-900 mb-3">{model.title}</h3>
                <p className="text-sm text-slate-600">{model.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal Clients Section */}
      <section className="section-padding bg-slate-50" data-testid="ideal-clients">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="badge badge-accent mb-6">Who We Serve</span>
              <h2 className="font-display text-4xl md:text-5xl text-slate-900 mb-8 tracking-tight">
                Ideal Client Profile
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-10">
                We work best with healthcare organizations that are ready for transformation, 
                not just consultation. Our clients are committed to building sustainable operations.
              </p>
              <div className="space-y-5">
                {idealClients.map((client, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-start gap-4"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check weight="bold" className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-700">{client}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-3xl p-10 shadow-lg border border-slate-100">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                    <Buildings weight="fill" className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-slate-900">Leadership Roles We Support</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    "CEOs & Owners",
                    "Executive Directors",
                    "Administrators",
                    "Directors of Clinical Services",
                    "Compliance Officers",
                    "Quality Directors",
                    "Operations Managers",
                    "Clinical Leaders"
                  ].map((role, index) => (
                    <motion.div 
                      key={index} 
                      className="flex items-center gap-3 py-2"
                      initial={{ opacity: 0, y: 5 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <UserCircle weight="duotone" className="w-5 h-5 text-cyan-500" />
                      <span className="text-sm text-slate-700">{role}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section - Dark Futuristic */}
      <section className="cta-section section-padding" data-testid="services-cta">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-32 h-32 border border-cyan-500/30 rounded-full" />
          <div className="absolute bottom-10 right-10 w-48 h-48 border border-cyan-500/30 rounded-full" />
        </div>
        
        <motion.div 
          className="absolute top-0 right-1/4 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.4) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="relative max-w-4xl mx-auto container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center mx-auto mb-8">
              <Sparkle weight="fill" className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-white mb-8 tracking-tight">
              Build a Sustainable Healthcare Organization
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-12">
              With the right operational strategy, regulatory structure, and intelligent systems, 
              hospice teams can deliver exceptional care while building sustainable organizations.
            </p>
            <Link
              to="/schedule-demo"
              className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold px-10 py-5 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/30 hover:-translate-y-1 inline-flex items-center justify-center gap-2"
              data-testid="services-final-cta"
            >
              Schedule a Strategy Consultation
              <ArrowRight weight="bold" className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
